import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { errorMessage } from "@/shared/i18n";
import { DemoButton, useDemo } from "@/shared/ui/DemoButton";
import { ConfirmDialog } from "@/shared/ui/Modal";
import { Section } from "@/shared/ui/Page";

export { DemoButton };

/** The system roles the demo company has someone in; its admins go in as any of them. */
const demoRoles = ["owner", "admin", "manager", "hr", "accountant", "sales_lead", "sales", "support", "cashier", "employee"];

/** Settings → Apps: what the demo company is, the way into it, and deleting it. */
export function DemoSettings() {
  const { t } = useTranslation();
  const demo = useDemo();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [role, setRole] = useState("owner");
  if (!demo.enabled) return null;
  const made = demo.state === "ready" || demo.state === "failed";
  return (
    <Section title={t("demo.title")}>
      <div className="flex flex-wrap items-center gap-3 p-4">
        <p className="min-w-0 flex-1 basis-72 text-ink-2">{t("demo.hint")}</p>
        <label className="flex items-center gap-2 whitespace-nowrap text-[13px] text-ink-2">
          {t("demo.as")}
          <select className="field max-w-44" value={role} onChange={(e) => setRole(e.target.value)}>
            {demoRoles.map((r) => <option key={r} value={r}>{t(`demo.roles.${r}`)}</option>)}
          </select>
        </label>
        <DemoButton className="btn-secondary" role={role} />
        {made && (
          <button type="button" className="btn-ghost text-danger" disabled={demo.opening} onClick={() => setConfirming(true)}>
            {t("demo.remove")}
          </button>
        )}
      </div>
      <ConfirmDialog open={confirming} title={t("demo.confirmTitle")} message={t("demo.confirmBody")} confirmLabel={t("demo.remove")} danger
        busy={busy} onClose={() => setConfirming(false)}
        onConfirm={async () => {
          setBusy(true);
          try {
            await demo.remove();
            toast.success(t("demo.removed"));
            setConfirming(false);
          } catch (e) {
            toast.error(errorMessage(e));
          } finally {
            setBusy(false);
          }
        }} />
    </Section>
  );
}

/** Opens the demo company as the role in ?as= (the demo's banner switches roles through
 *  here: opening happens from the real workspace). */
export function DemoOpenPage() {
  const { t } = useTranslation();
  const demo = useDemo();
  const [failed, setFailed] = useState(false);
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return; // StrictMode runs effects twice
    started.current = true;
    const role = new URLSearchParams(window.location.search).get("as") ?? "owner";
    if (!demo.enabled) setFailed(true);
    else void demo.open(role).then((ok) => setFailed(!ok));
  }, [demo]);
  return (
    <div className="grid min-h-dvh place-items-center bg-paper px-4">
      {failed ? (
        <a href="/" className="btn-secondary">{t("demo.back")}</a>
      ) : (
        <p className="flex items-center gap-2 text-ink-2"><Loader2 className="size-5 animate-spin text-ledger" /> {t("demo.preparing")}</p>
      )}
    </div>
  );
}

/** In the demo: whose eyes it's seen through, and seeing it as another role instead. */
export function DemoRoleSwitch({ back, current }: { back: string; current?: string }) {
  const { t } = useTranslation();
  return (
    <label className="flex items-center gap-2 whitespace-nowrap">
      {t("demo.seeAs")}
      <select className="field py-0.5 text-[13px]" value={current} onChange={(e) => window.location.assign(`${back}demo?as=${e.target.value}`)}>
        {demoRoles.map((r) => <option key={r} value={r}>{t(`demo.roles.${r}`)}</option>)}
      </select>
    </label>
  );
}
